import { CheckCircle, Loader2, Unplug } from 'lucide-preact'
import { CrmSlotTakenNotice } from '../components/CrmSlotTakenNotice'
import { crmBlockedDetail, crmBlockedHeadline } from '../crmSlot'
import { IntegrationProvider } from '@/types/integrations'

interface ConnectStepProps {
    isConnected: boolean
    isConnecting: boolean
    /** The CRM already holding the company's slot, when there is one. */
    blockedByCrm?: string | null
    onConnect: () => void
    onDisconnect: () => void
}

export function ConnectStep({ isConnected, isConnecting, blockedByCrm, onConnect, onDisconnect }: ConnectStepProps) {
    // Reached most often from a disconnected HubSpot's card, which keeps its Manage entry point
    // after another CRM takes the slot.
    if (!isConnected && blockedByCrm) {
        return (
            <CrmSlotTakenNotice
                headline={crmBlockedHeadline(blockedByCrm)}
                detail={crmBlockedDetail(blockedByCrm, IntegrationProvider.HubSpot)}
            />
        )
    }

    return (
        <div className="space-y-8">
            <div className="text-center space-y-3">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Connect to HubSpot</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                    Authorize OrbitalX to access your HubSpot CRM. This creates a secure OAuth connection at the company level.
                </p>
            </div>

            <div className="flex justify-center">
                {isConnected ? (
                    <div className="space-y-4 text-center">
                        <div className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300">
                            <CheckCircle className="w-5 h-5" />
                            <span className="text-sm font-medium">HubSpot Connected</span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Your HubSpot account is connected. Click Continue to configure your integration.
                        </p>
                        <button
                            onClick={onDisconnect}
                            className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                            <Unplug className="w-3 h-3" /> Disconnect
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={onConnect}
                        disabled={isConnecting}
                        className="inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-[#ff7a59] hover:bg-[#e8663e] text-white font-medium text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                    >
                        {isConnecting ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <img src={`${(import.meta as ImportMeta).env?.VITE_ASSETS_BASE_URL?.replace(/\/$/, '') || ''}/assets/integrations/hubspot-logo.svg`} alt="" className="w-6 h-6" />
                        )}
                        {isConnecting ? 'Connecting...' : 'Connect HubSpot'}
                    </button>
                )}
            </div>
        </div>
    )
}
